import Vue from 'vue'
import Vuex from 'vuex'
import store from 'store'
import orgStore from './modules/organization/store'
import spaceStore from './modules/space/store'
import instanceStore from './modules/instance/store'
import snapshotStore from './modules/snapshot/store'
import appStore from './modules/application/store'
import userStore from './modules/user/store'
import $axios from './backend.js'
import { globalDialogState, showGlobalDialog, hideGlobalDialog, clearGlobalDialogQueue } from './utils/globalDialogs'

Vue.use(Vuex)

export default new Vuex.Store({
    state: {
        snackBar: false,
        snackBarText: 'default text',
        snackBarIcon: 'check_circle',
        snackBarLabel: 'Close',
        ...globalDialogState(),
        userOrgs: [],
        currentOrgUsers: [],
        fetchingOrgs: false,
        currentOrg: 0,
        currentOrgData: null,
        userInfo: null,
        userMetadata: null,
        snackBarTimer: null,
        openOrgSelectorDialog: false,
        fetchingUserInfo: false,
        trialInfo: null,
        bundleCreated: false,
        dimmedSidebar: false
    },
    mutations: {
        showSnackBar: function (state, { snackBarText, snackBarIcon, snackBarLabel, snackBarButtonOutline }) {
            state.snackBar = true
            state.snackBarText = snackBarText
            state.snackBarIcon = snackBarIcon
            state.snackBarLabel = snackBarLabel
            state.snackBarButtonOutline = snackBarButtonOutline
        },
        showGlobalDialog,
        clearGlobalDialogQueue,
        setSnackBarTimer: function (state, timer) {
            state.snackBarTimer = timer
        },
        resetSnackBarTimer: function (state) {
            if (state.snackBarTimer) {
                clearTimeout(state.snackBarTimer)
            }
        },
        setUserOrgs: function (state, userOrgs) {
            state.userOrgs = userOrgs
        },
        setFetching: function (state, val) {
            state.fetchingOrgs = val
        },
        setOpenOrgSelectorDialog: function (state, val) {
            state.openOrgSelectorDialog = val
        },
        setCurrentOrgData: function (state, newData) {
            state.currentOrgData = newData
        },
        hideSnackBar: function (state) {
            state.snackBar = false
        },
        hideGlobalDialog,
        setFetchingUserInfo: function (state, status) {
            state.fetchingUserInfo = status
        },
        setUserInfo: function (state, { userInfo, userMetadata }) {
            state.userInfo = userInfo
            state.userMetadata = userMetadata
        },
        setUserMetadata: function (state, properties) {
            try {
                $axios
                    .patch('/users/metadata', {
                        user_metadata: properties
                    })
                    .then(() => {
                        state.userMetadata = { ...state.userMetadata, ...properties }
                    })
            } catch (error) {
                return error
            }
        },
        setTrialInfo: function (state, trialInfo) {
            state.trialInfo = trialInfo
        },
        setBundleCreated: function (state, val) {
            state.bundleCreated = val
        },
        toggleSidebarBrightness(state) {
            state.dimmedSidebar = !state.dimmedSidebar
        }
    },
    actions: {
        showSnackBar: function (context, { snackBarTimeout, snackBarText, snackBarIcon, snackBarLabel }) {
            context.commit('resetSnackBarTimer')
            context.commit('showSnackBar', { snackBarText, snackBarIcon, snackBarLabel })
            context.commit(
                'setSnackBarTimer',
                setTimeout(() => {
                    context.commit('hideSnackBar')
                }, snackBarTimeout || 10000)
            )
        },
        showGlobalDialog(context, { dialogTitle, dialogText, dialogAction, primaryAction, taskId, options, hash }) {
            context.commit('showGlobalDialog', { dialogTitle, dialogText, dialogAction, primaryAction, globalDialogTaskId: taskId, options, hash })
        },
        hideSnackBar: function (context) {
            context.commit('hideSnackBar')
        },
        hideGlobalDialog: function (context, id) {
            context.commit('hideGlobalDialog', id)
        },
        clearGlobalDialogQueue(context) {
            context.commit('clearGlobalDialogQueue')
        },
        fetchUserOrgs: function (context) {
            context.commit('setFetching', true)
            $axios
                .post('/orgs/find', {})
                .then(response => {
                    let orgData
                    const latestOrg = context.state.userMetadata?.latestOrg?.toString()
                    const oids = response.data.map(org => org.oid.toString())
                    if (context.state.currentOrgData && oids.includes(context.state.currentOrgData.oid.toString())) {
                        orgData = context.state.currentOrgData
                    } else if (latestOrg && oids.includes(latestOrg)) {
                        const latestOrgData = response.data.filter(org => org.oid.toString() === latestOrg)
                        orgData = latestOrgData.length ? latestOrgData[0] : response.data[0]
                    } else {
                        orgData = response.data[0]
                    }
                    context.commit('setCurrentOrgData', orgData)
                    context.commit('setUserOrgs', response.data)
                })
                .finally(() => {
                    context.commit('setFetching', false)
                })
        },
        setOpenOrgSelectorDialog: function (context, val) {
            context.commit('setOpenOrgSelectorDialog', val)
        },
        setCurrentOrgData: function (context, newData) {
            if (newData !== undefined) {
                if (context.state.userOrgs && context.state.userOrgs.length > 1 && !context.state.userMetadata?.latestOrg) {
                    context.commit('setOpenOrgSelectorDialog', true)
                }
                context.commit('setCurrentOrgData', newData)
                context.commit('setUserMetadata', { latestOrg: newData.oid })
            }
        },
        getTrialInfo(context) {
            return $axios
                .get('/trials/current')
                .then(response => {
                    context.commit('setTrialInfo', response.data)
                })
                .catch(error => {
                    context.commit('setTrialInfo', null)
                    return error
                })
        },
        getUserInfo(context) {
            context.commit('setFetchingUserInfo', true)
            return $axios
                .get('/users/current')
                .then(async response => {
                    let userMetadata = null
                    try {
                        const { data } = await $axios.get('/users/metadata')
                        userMetadata = data
                    } catch (error) {
                        context.commit('setUserInfo', { userInfo: null, userMetadata: null })
                    }
                    if (typeof response.data === 'object' && response.data.is_active) {
                        context.commit('setUserInfo', { userInfo: response.data, userMetadata })
                        if (response.data.email) {
                            store.set('nvLastEmail', response.data.email)
                        }
                    } else if (context.state.route.params.invid === undefined) {
                        context.commit('setUserInfo', { userInfo: null, userMetadata: null })
                    }
                    context.commit('setFetchingUserInfo', false)
                    if (response.data && response.data.is_trial_user) {
                        this.dispatch('getTrialInfo')
                    }
                })
                .catch(error => {
                    if (error.response) {
                        // Request made and server responded
                        console.log('ERROR RESPONSE(data):', error.response.data)
                        console.log('ERROR RESPONSE(status):', error.response.status)
                        console.log('ERROR RESPONSE(header):', error.response.headers)
                    } else if (error.request) {
                        // The request was made but no response was received
                        console.log('ERROR REQUEST:', error.request)
                    } else {
                        // Something happened in setting up the request that triggered an Error
                        console.log('ERROR ELSE:', error.message)
                    }
                    context.commit('setUserInfo', { userInfo: null, userMetadata: null })
                    context.commit('setFetchingUserInfo', false)
                })
        }
    },
    getters: {
        isSysadmin(state) {
            return state.userInfo && state.userInfo.is_sysadmin
        },
        userFullName(state) {
            if (!state.userInfo) return ''
            const fullName = `${state.userInfo.given_name || ''} ${state.userInfo.family_name || ''}`.trim()
            return fullName
        }
    },
    modules: {
        orgStore,
        spaceStore,
        instanceStore,
        snapshotStore,
        appStore,
        userStore
    }
})
